import type { Recall } from '../../../../types/RM'

export type RecallCta = {
  topic: string
  header: string
  body: string
  buttonLabel: string
  href: string
}

const REJECTED_BRAND_WORDS = new Set([
  'various',
  'certain',
  'some',
  'multiple',
  'several',
  'unknown',
  'no',
  'unbranded',
  'assorted',
])

const VEHICLE_BODY =
  'We email or text you when Transport Canada lists a new recall for the vehicle you name. Dealer and manufacturer letters can take weeks.'

const NON_VEHICLE_BODY =
  'We email or text you when Canada issues a recall that matches what you watch: a brand, a product, an allergen. Most never make the news.'

export function displayCase(value: string): string {
  const parts = value.split(/([ -])/)
  return parts
    .map(part => {
      if (part === ' ' || part === '-') {
        return part
      }
      if (/^[A-Z]+$/.test(part) && part.length >= 4) {
        return part.charAt(0) + part.slice(1).toLowerCase()
      }
      return part
    })
    .join('')
}

function topicFromCategory(category: string | undefined): string {
  if (category === '2') return 'vehicle'
  if (category === '1') return 'food'
  if (category === '3') return 'health'
  if (category === '4') return 'consumer'
  return 'all'
}

function categoryNoun(topic: string): string {
  if (topic === 'food') return 'food'
  if (topic === 'health') return 'health product'
  if (topic === 'consumer') return 'product'
  return ''
}

function sameIgnoreCase(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase()
}

function vehicleProducts(parsed: Record<string, unknown> | undefined) {
  if (!parsed) return []
  return Object.keys(parsed)
    .filter(key => /^product_/.test(key))
    .map(key => parsed[key] as Record<string, string | undefined>)
    .filter(p => p?.Make?.trim() && p?.Model?.trim())
}

function buildVehicleCta(recall: Recall, topic: string): RecallCta {
  const products = vehicleProducts(recall.parsed as Record<string, unknown>)
  const hrefBase = '/configure?topic=vehicle&mode=vehicle'

  if (products.length === 0) {
    return {
      topic,
      header: 'Get told about the next recall on your vehicle.',
      body: VEHICLE_BODY,
      buttonLabel: 'Get alerts for my vehicle',
      href: hrefBase,
    }
  }

  const firstMake = products[0].Make!.trim()
  const make = products.every(p => sameIgnoreCase(p.Make!.trim(), firstMake))
    ? firstMake
    : undefined

  const firstModel = products[0].Model!.trim()
  const model =
    make && products.every(p => sameIgnoreCase(p.Model!.trim(), firstModel))
      ? firstModel
      : undefined

  let year: string | undefined
  if (model) {
    const years = new Set<string>()
    for (const p of products) {
      const raw = p['Model year(s) affected']
      if (!raw) continue
      for (const part of raw.split(',')) {
        const y = part.trim()
        if (/^\d{4}$/.test(y)) years.add(y)
      }
    }
    if (years.size === 1) {
      year = Array.from(years)[0]
    }
  }

  const params = new URLSearchParams()
  params.set('topic', 'vehicle')
  params.set('mode', 'vehicle')
  if (make) params.set('make', make)
  if (model) params.set('model', model)
  if (year) params.set('year', year)
  const href = `/configure?${params.toString()}`

  if (!make) {
    return {
      topic,
      header: 'Get told about the next recall on your vehicle.',
      body: VEHICLE_BODY,
      buttonLabel: 'Get alerts for my vehicle',
      href: hrefBase,
    }
  }

  const displayParts: string[] = []
  if (year) displayParts.push(year)
  displayParts.push(displayCase(make))
  if (model) displayParts.push(displayCase(model))
  const name = displayParts.join(' ')

  return {
    topic,
    header: `Get told about the next recall on your ${name}.`,
    body: VEHICLE_BODY,
    buttonLabel: `Get alerts for my ${name}`,
    href,
  }
}

function extractBrand(title: string | undefined): string | undefined {
  if (!title) return undefined
  const match = title.match(/^(.+?)\s+brand\b/i)
  if (!match) return undefined
  const candidate = match[1].trim()
  if (candidate.length < 2 || candidate.length > 30) return undefined
  const words = candidate.split(/\s+/)
  if (words.length > 4) return undefined
  if (!/^[A-Za-z0-9À-ÖØ-öø-ÿ][A-Za-z0-9À-ÖØ-öø-ÿ '’&-]*$/.test(candidate))
    return undefined
  const firstWord = words[0]?.toLowerCase()
  if (firstWord && REJECTED_BRAND_WORDS.has(firstWord)) return undefined
  return candidate
}

function buildNonVehicleCta(recall: Recall, topic: string): RecallCta {
  const title = recall.parsed?.title as string | undefined
  const brand = extractBrand(title)
  const noun = categoryNoun(topic)

  if (brand) {
    const q = encodeURIComponent(brand)
    const href =
      topic === 'all' ? `/configure?q=${q}` : `/configure?topic=${topic}&q=${q}`
    return {
      topic,
      header: `Get told about the next ${brand} recall.`,
      body: NON_VEHICLE_BODY,
      buttonLabel: `Get alerts for ${brand} recalls`,
      href,
    }
  }

  if (topic === 'all') {
    return {
      topic,
      header: 'Get told about the next recall.',
      body: NON_VEHICLE_BODY,
      buttonLabel: 'Get recall alerts',
      href: '/configure',
    }
  }

  return {
    topic,
    header: `Get told about the next ${noun} recall.`,
    body: NON_VEHICLE_BODY,
    buttonLabel: `Get ${noun} recall alerts`,
    href: `/configure?topic=${topic}`,
  }
}

export function buildRecallCta(recall: Recall): RecallCta {
  const category = recall.category?.[0]
  const topic = topicFromCategory(category)

  if (category === '2') {
    return buildVehicleCta(recall, topic)
  }

  return buildNonVehicleCta(recall, topic)
}
